import React, { forwardRef, useEffect, useRef } from 'react';
import Classnames from 'classnames';
import { TagsFormFilterField, TagsFormFilterFieldWidth, TagsFormFiltersInputTypes } from '../types';
import { InputWrapper } from '../../../ui-inputs/input-wrapper';
import { GenericDropdown } from '../../../ui-widgets/generic-dropdown/generic-dropdown';
import { Checkbox } from '../../../form-components';
import { Button, ButtonColor, ButtonSize } from '../../../buttons';
import { InputTypes } from '../../../ui-inputs/validated-input';
import { getEventInterceptors, submitFormOnEnter } from '../../../../../utils';
import { useRefAssigner } from '../../../../../hooks/use-ref-assigner';
import { DYNAMIC_REACT_PORTAL_Z_INDEX_CLASS } from '../../../dynamic-react-portal';

import './tags-form-popover.scss';

interface TagsFormPopoverProps<T extends Record<string, any>> {
    shown: boolean;
    fields: TagsFormFilterField[];
    closePopover: () => void;
    handleFieldChange: (key: string, value: any) => void;
    applyFilters: () => void;
    clearFilters: () => void;
    tempFilters: T;
}

export const TagsFormPopover = forwardRef(function <T extends Record<string, any>>(
    {
        shown,
        fields,
        closePopover,
        handleFieldChange,
        applyFilters,
        clearFilters,
        tempFilters,
    }: TagsFormPopoverProps<T>,
    ref: React.RefCallback<HTMLDivElement> | React.MutableRefObject<HTMLDivElement>,
): React.ReactElement {
    const popoverRef = useRef(null);

    const { addDocumentListeners, removeDocumentListeners } = getEventInterceptors(popoverRef, closePopover);

    useEffect(() => {
        addDocumentListeners();

        return () => {
            removeDocumentListeners();
        };
    }, []);

    const classnames = Classnames('tags-form-popover', {
        [`tags-form-popover--shown ${DYNAMIC_REACT_PORTAL_Z_INDEX_CLASS}`]: shown,
    });

    const refAssigner = useRefAssigner<HTMLDivElement>(ref, popoverRef);

    const keyIntercept = (evt: React.KeyboardEvent) => {
        if (evt.type === 'keydown' || evt.type === 'keyup') {
            evt.stopPropagation();

            if (evt.key === 'Escape') {
                closePopover();
            }
        }
    };

    const interceptKeys = {
        onKeyDown: keyIntercept,
        onKeyUp: keyIntercept,
    };

    return (
        <div id="advanced-search-form-popup" role="dialog" ref={refAssigner} className={classnames} {...interceptKeys}>
            <h3>Search</h3>
            <div className="tags-form-popover__fields">
                {fields.map((field) => {
                    const classes = Classnames(
                        'tags-form-popover__field-wrapper',
                        `tags-form-popover__field-wrapper--${field.type}`,
                        `tags-form-popover__field-wrapper--width-${field.width || TagsFormFilterFieldWidth.FULL}`,
                    );

                    const firstInputFieldKey = fields.find(
                        (field) => field.type === TagsFormFiltersInputTypes.INPUT,
                    )?.key;

                    return (
                        <div className={classes} key={`field-${field.key}`}>
                            {field.type === TagsFormFiltersInputTypes.INPUT && (
                                <>
                                    <InputWrapper
                                        inputId={field.key}
                                        labelText={field.label}
                                        useErrorAsLabel
                                        inputProps={{
                                            type: field.inputType || InputTypes.TEXT,
                                            name: field.key,
                                            placeholder: field.placeholder,
                                            value: tempFilters[field.key] || '',
                                            onChange: (e) => handleFieldChange(field.key, e.target.value),
                                            onKeyDown: (event) => submitFormOnEnter(event, applyFilters),
                                            autoFocus: field.key === firstInputFieldKey, // Set focus on first input in pop up
                                        }}
                                    />
                                </>
                            )}
                            {field.type === TagsFormFiltersInputTypes.DROPDOWN && (
                                <GenericDropdown
                                    testId={`dropdown-${field.key}`}
                                    className="tags-form-popover__dropdown"
                                    label={field.label}
                                    onChange={(value: string | number) => handleFieldChange(field.key, value)}
                                    options={field.options}
                                    selected={tempFilters[field.key]}
                                />
                            )}
                            {field.type === TagsFormFiltersInputTypes.CHECKBOX && (
                                <Checkbox
                                    key={field.label}
                                    id={`checkbox-${field.key}`}
                                    title={field.label}
                                    content={field.label}
                                    onChange={(newValue: boolean) => handleFieldChange(field.key, newValue)}
                                    isChecked={tempFilters[field.key]}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="tags-form-popover__controls">
                <Button color={ButtonColor.SECONDARY} size={ButtonSize.MINI} onClick={clearFilters}>
                    Clear
                </Button>
                <Button color={ButtonColor.PRIMARY} size={ButtonSize.MINI} onClick={applyFilters} icon="fas fa-search">
                    Search
                </Button>
            </div>
        </div>
    );
});
